import { useQuery } from "@tanstack/solid-query"
import { createFileRoute, isMatch, Link, Outlet, useMatches } from "@tanstack/solid-router"
import Book from "lucide-solid/icons/book"
import Car from "lucide-solid/icons/car"
import Folder from "lucide-solid/icons/folder"
import Home from "lucide-solid/icons/home"
import Settings from "lucide-solid/icons/settings"
import User from "lucide-solid/icons/user"
import { createSignal, For, Match, Show, Switch } from "solid-js"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator
} from "~/components/ui/breadcrumb"
import { Separator } from "~/components/ui/separator"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  sidebarMenuButtonVariants
} from "~/components/ui/sidebar"
import { orpc } from "~/lib/orpc"
import { isFiveMBrowser } from "~/lib/utils"
import { HeaderCard } from "./-components/HeaderCard"
import { UserCard } from "./-components/UserCard"

export const Route = createFileRoute("/_authenticated/tablet/$orgId/_tablet")({
  beforeLoad: async ({ params }) => {
    await orpc.organization.validateOrg.call({ orgId: params.orgId })
  },
  loader: ({ context }) => {
    context.queryClient.prefetchQuery(orpc.organization.getActiveCharacter.queryOptions())
    return { crumb: "Home" }
  },
  component: () => TabletLayout
})

function TabletLayout() {
  const [isParentHovered, setIsParentHovered] = createSignal(false)
  const [isChildHovered, setIsChildHovered] = createSignal(false)
  const tabletShouldBeHovered = () => isParentHovered() && !isChildHovered()

  return (
    <Switch>
      <Match when={isFiveMBrowser()}>
        <div
          class="flex h-screen items-center justify-center p-8 transition-opacity duration-300"
          on:mouseenter={() => setIsParentHovered(true)}
          on:mouseleave={() => setIsParentHovered(false)}
        >
          <div
            class="relative h-[90vh] w-[90vw] overflow-hidden rounded-2xl border border-border shadow-2xl"
            on:mouseenter={() => setIsChildHovered(true)}
            on:mouseleave={() => setIsChildHovered(false)}
            classList={{ "opacity-50": tabletShouldBeHovered(), "opacity-100": !tabletShouldBeHovered() }}
          >
            <Tablet />
          </div>
        </div>
      </Match>
      <Match when={!isFiveMBrowser()}>
        <div class="flex h-screen items-center justify-center">
          <Tablet />
        </div>
      </Match>
    </Switch>
  )
}

function Tablet() {
  const params = Route.useParams()
  const matches = useMatches()

  const character = useQuery(() => orpc.organization.getActiveCharacter.queryOptions())

  const items = {
    main: [
      {
        title: "Home",
        url: `/tablet/${params().orgId}`,
        icon: Home
      },
      {
        title: "Citizens",
        url: `/tablet/${params().orgId}/citizens`,
        icon: User
      },
      {
        title: "Vehicles",
        url: `/tablet/${params().orgId}/vehicles`,
        icon: Car
      },
      {
        title: "Cases",
        url: `/tablet/${params().orgId}/cases`,
        icon: Folder
      },
      {
        title: "Employees",
        url: `/tablet/${params().orgId}/employees`,
        icon: User
      },
      {
        title: "Offenses",
        url: `/tablet/${params().orgId}/offenses`,
        icon: Book
      }
    ],
    secondary: [
      {
        title: "Permissions",
        url: `/tablet/${params().orgId}/permissions`,
        icon: User
      },
      {
        title: "Settings",
        url: `/tablet/${params().orgId}/settings`,
        icon: Settings
      }
    ]
  }

  return (
    <SidebarProvider class="h-full min-h-full">
      <Sidebar collapsible="icon" variant="floating">
        <SidebarHeader>
          <HeaderCard />
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <For each={items.main}>
                  {(item) => (
                    <SidebarMenuItem>
                      <Link to={item.url} class={sidebarMenuButtonVariants({ class: "flex items-center gap-3" })}>
                        <item.icon class="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuItem>
                  )}
                </For>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          <SidebarGroup class="mt-auto">
            <SidebarGroupContent>
              <SidebarMenu>
                <For each={items.secondary}>
                  {(item) => (
                    <SidebarMenuItem>
                      <Link to={item.url} class={sidebarMenuButtonVariants({ class: "flex items-center gap-3" })}>
                        <item.icon class="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuItem>
                  )}
                </For>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          {/* User Card */}
          <Show when={character.isSuccess}>
            <UserCard player={character.data!} />
          </Show>
        </SidebarFooter>
      </Sidebar>
      <SidebarInset class="h-full min-h-full min-w-0 gap-2 p-2 md:pl-0">
        <header class="flex h-10 shrink-0 items-center gap-2 rounded-lg border border-sidebar-border bg-sidebar bg-opacity-90 px-2 shadow">
          <SidebarTrigger class="-ml-1 shrink-0" />
          <Separator orientation="vertical" class="mr-2 h-4 shrink-0" />
          <Breadcrumb>
            <BreadcrumbList class="h-8">
              <For each={matches().filter((match) => isMatch(match, "loaderData.crumb"))}>
                {(crumb, i) => (
                  <BreadcrumbItem>
                    {matches().filter((match) => isMatch(match, "loaderData.crumb")).length - 1 === i() ? (
                      <BreadcrumbLink current>{crumb.loaderData!.crumb}</BreadcrumbLink>
                    ) : (
                      <>
                        <BreadcrumbLink as={Link} to={crumb.pathname}>
                          {crumb.loaderData!.crumb}
                        </BreadcrumbLink>
                        <BreadcrumbSeparator />
                      </>
                    )}
                  </BreadcrumbItem>
                )}
              </For>
            </BreadcrumbList>
          </Breadcrumb>

          {/* <div class="flex min-w-0 flex-grow items-center gap-2 overflow-hidden">
            <div class="flex items-center gap-2 overflow-x-auto">
              <For each={tabs()}>
                {(tab, i) => (
                  <Button
                    variant="outline"
                    class="h-8 gap-2 pr-2"
                    onClick={() => console.log("Switch to tab", tab.title)}
                  >
                    <span>{tab.title}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      class="h-5 w-5 hover:bg-red-700"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        const newTabs = tabs().filter((_, index) => index !== i())
                        setTabs(newTabs)
                      }}
                    >
                      <TbX class="h-5 w-5" />
                    </Button>
                  </Button>
                )}
              </For>
            </div>
            <Button
              variant="ghost"
              size="icon"
              class="size-7 shrink-0"
              onClick={() =>
                setTabs([...tabs(), { title: `Tab ${tabs().length + 1}`, url: `/tab${tabs().length + 1}` }])
              }
            >
              <TbPlus />
            </Button>
            <ModeToggle />
          </div> */}
        </header>
        <div class="h-full overflow-y-auto">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
